import { z } from "zod";

const messageTypes = [
  "standard_message",
  "request_email",
  "error",
  "close_chat",
  "connecting_to_agent",
  "agent_joined",
  "file_uploaded",
  "file",
  "form_single_select",
  "first_request_email",
] as const;
export type MessageType = (typeof messageTypes)[number];

const authorTypes = ["AUTO_AGENT", "USER", "LIVE_AGENT", "SYSTEM"] as const;
export type AuthorType = (typeof authorTypes)[number];

export interface BaseMessage {
  action: MessageType;
  author: AuthorType;
  deduplicationKey: string;
  text: string;
  createdAt: string;
  messageScore?: number | null;
}

export interface StandardMessage extends BaseMessage {
  action: "standard_message";
  data: {
    agentDisplayName?: string;
    formId?: string;
  };
}

export interface RequestEmailMessage extends BaseMessage {
  action: "request_email";
  author: "SYSTEM";
  text: string;
  data: {
    defaultEmail?: string;
  };
}

export interface RequestFirstEmailMessage extends BaseMessage {
  action: "first_request_email";
  author: "SYSTEM";
  text: string;
  data: {
    defaultEmail?: string;
  };
}

export interface ErrorMessage extends BaseMessage {
  action: "error";
  author: "SYSTEM";
  data: {
    errorType: string;
    errorMessage: string;
  };
}

export interface CloseChatMessage extends BaseMessage {
  action: "close_chat";
  author: "SYSTEM";
  data: {
    showCSAT?: boolean;
  };
}

export interface ConnectingToAgentMessage extends BaseMessage {
  action: "connecting_to_agent";
  author: "SYSTEM";
  data: {};
}

export interface AgentJoinedMessage extends BaseMessage {
  action: "agent_joined";
  author: "SYSTEM";
  data: {
    agentDisplayName?: string;
  };
}

export interface FileUploadedMessage extends BaseMessage {
  action: "file_uploaded";
  author: "SYSTEM";
  data: {
    url: string;
    filename: string;
  };
}

export interface FileMessage extends BaseMessage {
  action: "file";
  author: "LIVE_AGENT" | "USER";
  data: {
    url: string;
    filename: string;
    agentDisplayName?: string;
  };
}

export interface FormSingleSelectMessage extends BaseMessage {
  action: "form_single_select";
  author: "SYSTEM";
  data: {
    options: string[];
    formId: string;
  };
}

export type Message =
  | StandardMessage
  | RequestEmailMessage
  | ErrorMessage
  | CloseChatMessage
  | ConnectingToAgentMessage
  | AgentJoinedMessage
  | FileUploadedMessage
  | FileMessage
  | FormSingleSelectMessage
  | RequestFirstEmailMessage;

const baseMessageSchema = {
  action: z.enum(messageTypes),
  author: z.enum(authorTypes),
  deduplicationKey: z.string(),
  text: z.string(),
  createdAt: z.string(),
  messageScore: z.number().nullable().optional(),
};

const standardMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("standard_message"),
  data: z.object({
    agentDisplayName: z.string().optional(),
    formId: z.string().optional(),
  }),
});

const requestEmailMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("request_email"),
  author: z.literal("SYSTEM"),
  data: z.object({
    defaultEmail: z.string().optional(),
  }),
});

const errorMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("error"),
  author: z.literal("SYSTEM"),
  data: z.object({
    errorType: z.string(),
    errorMessage: z.string(),
  }),
});

const closeChatMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("close_chat"),
  author: z.literal("SYSTEM"),
  data: z.object({
    showCSAT: z.boolean().optional(),
  }),
});

const connectingToAgentMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("connecting_to_agent"),
  author: z.literal("SYSTEM"),
  data: z.object({}),
});

const agentJoinedMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("agent_joined"),
  author: z.literal("SYSTEM"),
  data: z.object({
    agentDisplayName: z.string().optional(),
  }),
});

const fileUploadedMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("file_uploaded"),
  author: z.literal("SYSTEM"),
  data: z.object({
    url: z.string(),
    filename: z.string(),
  }),
});

const fileMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("file"),
  author: z.enum(["LIVE_AGENT", "USER"]),
  data: z.object({
    url: z.string(),
    filename: z.string(),
    agentDisplayName: z.string().optional(),
  }),
});

const formSingleSelectMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("form_single_select"),
  author: z.literal("SYSTEM"),
  data: z.object({
    options: z.array(z.string()),
    formId: z.string(),
  }),
});

const firstRequestEmailMessageSchema = z.object({
  ...baseMessageSchema,
  action: z.literal("first_request_email"),
  author: z.literal("SYSTEM"),
  data: z.object({
    defaultEmail: z.string().optional(),
  }),
});

export const messageSchema = z.discriminatedUnion("action", [
  standardMessageSchema,
  requestEmailMessageSchema,
  errorMessageSchema,
  closeChatMessageSchema,
  connectingToAgentMessageSchema,
  agentJoinedMessageSchema,
  fileUploadedMessageSchema,
  fileMessageSchema,
  formSingleSelectMessageSchema,
  firstRequestEmailMessageSchema,
]);

export function getAuthorInitials(author?: string) {
  if (!author) {
    return;
  }

  const words = author.split(" ").map((x) => x[0]);

  if (words.length === 1) {
    return words[0];
  }

  return [words[0], words[words.length - 1]].join("");
}
